// src/components/recovery/CheckinCard.tsx
import React from "react";
import { FiChevronRight } from "react-icons/fi";
import { createLogger } from "../../lib/logger";

const log = createLogger("CheckinCard");

interface CheckinCardProps {
  /** Background color for the icon circle (e.g. from the week's banner palette) */
  color?: string;
  /** Icon (emoji) to display */
  icon: string;
  /** Title text */
  title: string;
  /** Click handler */
  onClick: () => void;
  /** Whether the card is read-only */
  isReadOnly?: boolean;
  /** Whether check-in is complete for today */
  isComplete?: boolean;
}

/**
 * Card for daily check-in items (mood, symptoms, bleeding)
 */
export const CheckinCard: React.FC<CheckinCardProps> = React.memo(({
  icon,
  title,
  onClick,
  color,
  isReadOnly = false,
  isComplete = false,
}) => {
  const baseClasses = "w-full flex items-center justify-between p-3 rounded-2xl bg-white border-none transition-all duration-200";
  const interactiveClasses = isReadOnly 
    ? "cursor-default opacity-75" 
    : "cursor-pointer hover:bg-gray-50 active:scale-[0.98]";

  return (
    <button
      type="button"
      onClick={!isReadOnly ? () => { log.info("Check-in tapped", { title, isComplete }); onClick(); } : undefined}
      disabled={isReadOnly}
      className={`${baseClasses} ${interactiveClasses}`}
    >
      <div className="flex items-center gap-2.5">
        {/* Icon circle */}
        <div style={{
    width: 50,
    height: 50,
    borderRadius: "50%",
    backgroundColor: color,
  }}>
        <div className="w-[50px] h-[50px] rounded-full flex items-center justify-center text-[28px] flex-shrink-0">
          <img src={icon} alt={`${title} icon`} className="w-[28px] h-[28px]" />
        </div>
        </div>
        
        {/* Title */}
        <span className={`text-xl font-semibold font-dm-sans ${isComplete ? "text-brand-primary" : "text-brand-dark"}`}>
          {title}
        </span>
      </div>

      {/* Right side indicator */}
      {isComplete ? (
        <div className="w-6 h-6 rounded-full bg-brand-primary flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      ) : (
        <FiChevronRight size={20} className="text-gray-400" />
      )}
    </button>
  );
});

CheckinCard.displayName = "CheckinCard";

export default CheckinCard;
